import React, { useState, useEffect, useRef } from "react";
import { FaBars, FaTimes, FaUsers, FaGift, FaCalendarAlt, FaArrowRight, FaCrown, FaCheckCircle } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import axios from "axios";

function StoreCardsDisplay() {
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [cards, setCards] = useState([]);
  const [contents, setContents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingContent, setLoadingContent] = useState(true);
  const [error, setError] = useState(null);
  const headerRef = useRef(null);

  // Backend API base URL
  const API_BASE_URL = "https://backend.dlapakistan.com/api";

  // Fetch store content
  useEffect(() => {
    const fetchStoreContent = async () => {
      try {
        setLoadingContent(true);
        const response = await axios.get(`${API_BASE_URL}/store-content/?published_only=true`);
        
        let contentData = [];
        if (response.data.success && Array.isArray(response.data.data)) {
          contentData = response.data.data;
        } else if (Array.isArray(response.data)) {
          contentData = response.data;
        } else if (response.data.results && Array.isArray(response.data.results)) {
          contentData = response.data.results;
        }
        
        // Sort by created_at in ascending order (oldest first)
        const sortedContents = [...contentData].sort((a, b) => 
          new Date(a.created_at) - new Date(b.created_at)
        );
        
        setContents(sortedContents);
      } catch (err) {
        console.error("Error fetching store content:", err);
      } finally {
        setLoadingContent(false);
      }
    };

    fetchStoreContent();
  }, []);

  // Fetch store cards
  useEffect(() => {
    const fetchStoreCards = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await axios.get(`${API_BASE_URL}/store-cards/`);
        
        let cardsData = [];
        if (response.data.success && Array.isArray(response.data.data)) {
          cardsData = response.data.data;
        } else if (Array.isArray(response.data)) {
          cardsData = response.data;
        } else if (response.data.results && Array.isArray(response.data.results)) {
          cardsData = response.data.results;
        } else {
          cardsData = [];
        }
        
        const sortedCards = [...cardsData].sort((a, b) => 
          parseFloat(a.monthly_fee || 0) - parseFloat(b.monthly_fee || 0)
        );
        
        setCards(sortedCards);
        
        if (sortedCards.length === 0) {
          setError("No premium cards available. Please check back later.");
        }
      } catch (err) {
        console.error("Error fetching store cards:", err);
        
        if (err.response?.status === 404) {
          setError("Store cards API not found. Please check if the backend is running.");
        } else if (err.code === 'ERR_NETWORK') {
          setError("Cannot connect to server. Please make sure the backend is running on port 8855.");
        } else {
          setError("Failed to load premium cards. Please try again later.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchStoreCards();
  }, []);

  useEffect(() => {
    const updateHeaderHeight = () => {
      const header = document.querySelector('header') || 
                    document.querySelector('.header') ||
                    document.querySelector('#header');
      
      if (header) {
        setHeaderHeight(header.offsetHeight);
      } else {
        if (windowWidth < 640) {
          setHeaderHeight(80);
        } else if (windowWidth < 768) {
          setHeaderHeight(96);
        } else if (windowWidth < 1024) {
          setHeaderHeight(112);
        } else {
          setHeaderHeight(120);
        }
      }
    };

    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      updateHeaderHeight();
      if (window.innerWidth > 1024) {
        setIsSidebarOpen(false);
      }
    };

    updateHeaderHeight();
    window.addEventListener("resize", handleResize);
    
    const observer = new ResizeObserver(updateHeaderHeight);
    const headerElement = headerRef.current || document.querySelector('header');
    if (headerElement) observer.observe(headerElement);

    return () => {
      window.removeEventListener("resize", handleResize);
      if (headerElement) observer.unobserve(headerElement);
    };
  }, [windowWidth]);

  const handleProceedToPayment = (cardId, cardName, cardPrice) => {
    localStorage.setItem('selected_card', JSON.stringify({
      id: cardId,
      name: cardName,
      monthly_fee: cardPrice,
      selected_plan: cardName
    }));
    navigate(`/payment/${cardId}`);
  };

  const formatCurrency = (amount) => {
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount)) return 'PKR 0';
    return new Intl.NumberFormat('en-PK', {
      style: 'currency',
      currency: 'PKR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getPopularBadge = (index, totalCards) => {
    if (totalCards === 3 && index === 1) return true;
    if (totalCards > 3 && index === Math.floor(totalCards / 2)) return true;
    return false;
  };

  const isMobileView = windowWidth <= 1024;
  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);
  const mobileOverlayClass = isMobileView && isSidebarOpen ? "pt-0" : "";

  // Get the first content (to show on top)
  const firstContent = contents.length > 0 ? contents[0] : null;
  
  // Get the last content (to show at the bottom)
  const lastContent = contents.length > 0 ? contents[contents.length - 1] : null;
  
  // Get remaining contents (from index 1 to second last) for the two-column layout
  const remainingContents = contents.slice(1, contents.length - 1);
  
  // Function to split remaining contents into two columns (left and right)
  const getColumnContents = () => {
    const leftColumn = [];
    const rightColumn = [];
    
    remainingContents.forEach((content, index) => {
      if (index % 2 === 0) {
        leftColumn.push(content);
      } else {
        rightColumn.push(content);
      }
    });
    
    return { leftColumn, rightColumn };
  };

  const { leftColumn, rightColumn } = getColumnContents();

  return (
    <div 
      className={`w-full min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 ${mobileOverlayClass}`}
      ref={headerRef}
    >
      <div className="w-full px-4 mt-52 sm:px-6 md:px-8 lg:px-10 xl:px-12 mx-auto">
        
        {/* Breadcrumb section */}
        <div className="flex flex-wrap items-center text-gray-500 mb-4 md:mb-6 text-sm md:text-base">
          <span 
            className="cursor-pointer hover:underline hover:text-[#70B444] transition-colors"
            onClick={() => navigate("/")}
          >
            Home
          </span>
          <span className="mx-2">»</span>
          <span className="text-[#70B444] font-medium">
            Premium Store Cards
          </span>
        </div>

        {/* Header Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Choose Your <span className="text-[#70B444]">Premium Plan</span>
          </h1>
        </div>

        {/* First Content - Displayed on top (right after heading) - CENTER ALIGNED */}
        {!loadingContent && firstContent && (
          <div className="max-w-4xl mx-auto mb-12 text-center">
            <div 
              className="text-gray-700 store-content-wrapper first-content"
              style={{ fontSize: '1.1rem', lineHeight: '1.7' }}
              dangerouslySetInnerHTML={{ __html: firstContent.content }}
            />
          </div>
        )}

        {/* Remaining Contents - Two Column Layout with Divider */}
        {!loadingContent && remainingContents.length > 0 && (
          <div className="max-w-6xl mx-auto mb-16">
            <div className="flex flex-col md:flex-row gap-8">
              {/* Left Column */}
              <div className="flex-1 space-y-8">
                {leftColumn.map((content, idx) => (
                  <div key={content.id} className="store-content-item">
                    <div 
                      className="text-gray-700 store-content-wrapper"
                      style={{ fontSize: '1.1rem', lineHeight: '1.7' }}
                      dangerouslySetInnerHTML={{ __html: content.content }}
                    />
                  </div>
                ))}
              </div>
              
              {/* Vertical Divider Line - Only show on desktop */}
              <div className="hidden md:block w-px bg-gray-300 self-stretch"></div>
              
              {/* Right Column */}
              <div className="flex-1 space-y-8">
                {rightColumn.map((content, idx) => (
                  <div key={content.id} className="store-content-item">
                    <div 
                      className="text-gray-700 store-content-wrapper"
                      style={{ fontSize: '1.1rem', lineHeight: '1.7' }}
                      dangerouslySetInnerHTML={{ __html: content.content }}
                    />
                  </div>
                ))}
              </div>
            </div>
            
            {/* Horizontal Divider for Mobile */}
            <div className="md:hidden mt-6 pt-6 border-t border-gray-300"></div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="text-center py-16">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#70B444]"></div>
            <p className="mt-4 text-gray-600">Loading premium plans...</p>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="max-w-2xl mx-auto bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-lg mb-6">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex-1">
                <p className="font-medium">Error Loading Plans</p>
                <p className="text-sm mt-1">{error}</p>
                <button 
                  onClick={() => window.location.reload()}
                  className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Premium Cards Grid */}
        {!loading && !error && cards.length > 0 && (
          <div className="flex justify-center items-stretch">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl w-full">
              {cards.map((card, index) => {
                const isPopular = getPopularBadge(index, cards.length);
                return (
                  <div 
                    key={card.id}
                    className={`relative bg-white rounded-2xl overflow-hidden transition-all duration-300 flex flex-col h-full group cursor-pointer
                      ${isPopular 
                        ? 'shadow-2xl scale-105 border-2 border-[#70B444] ring-2 ring-[#70B444]/20' 
                        : 'shadow-lg hover:shadow-2xl hover:scale-105 border border-gray-200'
                      }`}
                    onClick={() => handleProceedToPayment(card.id, card.lab_category_name, card.monthly_fee)}
                  >
                    {/* Arrow Button - Top Right */}
                    <div className="absolute top-4 right-4 z-20 bg-white/90 backdrop-blur-sm p-2 rounded-full shadow-md transition-all duration-300 group-hover:scale-110 group-hover:bg-[#70B444]">
                      <FaArrowRight className="text-[#70B444] text-sm group-hover:text-white transition-colors" />
                    </div>

                    {/* Popular Badge */}
                    {isPopular && (
                      <div className="absolute top-0 left-0 z-10">
                        <div className="bg-gradient-to-r from-[#70B444] to-[#538630] text-white px-4 py-1 rounded-br-2xl text-sm font-bold flex items-center gap-1">
                          <FaCrown className="text-yellow-300 text-xs" />
                          MOST POPULAR
                        </div>
                      </div>
                    )}

                    {/* Card Header */}
                    <div className={`p-6 text-center pt-8 ${isPopular ? 'bg-gradient-to-r from-[#70B444] to-[#538630]' : 'bg-gray-50'}`}>
                      <h3 className={`text-2xl font-bold mb-2 ${isPopular ? 'text-white' : 'text-gray-800'}`}>
                        {card.lab_category_name}
                      </h3>
                      <div className={`flex items-center justify-center gap-2 ${isPopular ? 'text-white/90' : 'text-gray-600'}`}>
                        <FaUsers className="text-sm" />
                        <span className="text-sm">{card.staff_size}</span>
                      </div>
                    </div>

                    {/* Pricing */}
                    <div className="p-6 text-center border-b border-gray-100">
                      <div className="mb-2">
                        <span className="text-5xl font-bold text-gray-900">
                          {formatCurrency(card.monthly_fee).replace('PKR', '')}
                        </span>
                        <span className="text-gray-500 text-base ml-1">/month</span>
                      </div>
                      <div className="text-gray-500 text-sm">
                        <span>Billed annually </span>
                        <span className="font-semibold text-[#70B444]">
                          {formatCurrency(card.annual_total)}
                        </span>
                        <span>/year</span>
                      </div>
                      <div className="mt-2 inline-block bg-green-100 text-green-700 text-xs font-semibold px-3 py-1 rounded-full">
                        Save 2 months FREE!
                      </div>
                    </div>

                    {/* Features List */}
                    <div className="p-6 flex-1">
                      <p className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                        <FaGift className="text-[#70B444]" />
                        What's included:
                      </p>
                      <div className="space-y-3">
                        {card.key_perks.split('\n').filter(perk => perk && perk.trim()).slice(0, 6).map((perk, idx) => (
                          <div key={idx} className="flex items-start gap-2">
                            <FaCheckCircle className="text-[#70B444] text-sm mt-0.5 flex-shrink-0" />
                            <span className="text-gray-600 text-sm leading-relaxed">
                              {perk.replace('✓', '').replace('•', '').trim()}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Card Footer */}
                    <div className="p-4 bg-gray-50/50 border-t border-gray-100">
                      <div className="flex items-center justify-center gap-2 text-xs text-gray-400">
                        <FaCalendarAlt className="text-xs" />
                        <span>Updated {new Date(card.updated_at).toLocaleDateString('en-PK')}</span>
                      </div>
                    </div>

                    {/* Hover instruction */}
                    <div className="absolute top-20 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="bg-gray-800 text-white text-xs px-2 py-1 rounded shadow-lg whitespace-nowrap">
                        Click to select →
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && cards.length === 0 && (
          <div className="text-center py-16 bg-white rounded-lg border border-gray-200 max-w-2xl mx-auto">
            <div className="text-6xl mb-4">💳</div>
            <h3 className="text-2xl font-semibold text-gray-700 mb-2">No Premium Plans Available</h3>
            <p className="text-gray-600 mb-4">
              There are no premium store cards available at the moment. Please check back later.
            </p>
            <button 
              onClick={() => window.location.reload()}
              className="text-[#70B444] underline hover:no-underline text-sm"
            >
              Refresh page
            </button>
          </div>
        )}

        {/* Trust Section */}
        {!loading && !error && cards.length > 0 && (
          <div className="mt-16 text-center">
            <div className="flex flex-wrap justify-center items-center gap-8 text-gray-400">
              <div className="flex items-center gap-2">
                <FaCheckCircle className="text-[#70B444]" />
                <span className="text-sm">No hidden fees</span>
              </div>
              <div className="flex items-center gap-2">
                <FaCheckCircle className="text-[#70B444]" />
                <span className="text-sm">Cancel anytime</span>
              </div>
              <div className="flex items-center gap-2">
                <FaCheckCircle className="text-[#70B444]" />
                <span className="text-sm">24/7 support</span>
              </div>
              <div className="flex items-center gap-2">
                <FaCheckCircle className="text-[#70B444]" />
                <span className="text-sm">Secure payments</span>
              </div>
            </div>
          </div>
        )}

        {/* Last Content - Displayed at the bottom (after cards and trust section) - LEFT ALIGNED with larger text */}
        {!loadingContent && lastContent && contents.length > 1 && (
          <div className="max-w-6xl mx-auto mt-16 pt-8">
            <div 
              className="text-gray-700 store-content-wrapper last-content"
              style={{ fontSize: '1.1rem', lineHeight: '1.7' }}
              dangerouslySetInnerHTML={{ __html: lastContent.content }}
            />
          </div>
        )}
      </div>

      {/* Mobile Overlay */}
      {isMobileView && isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-[50] transition-opacity"
          style={{ top: `${headerHeight}px` }}
          onClick={toggleSidebar}
        />
      )}

      {/* Styles for store content */}
      <style jsx>{`
        .store-content-wrapper {
          line-height: 1.7;
          color: #4b5563;
        }
        
        .first-content {
          text-align: center;
        }
        
        .first-content p,
        .first-content h1,
        .first-content h2,
        .first-content h3,
        .first-content h4,
        .first-content h5,
        .first-content h6,
        .first-content div,
        .first-content span,
        .first-content ul,
        .first-content ol {
          text-align: center;
          margin-left: auto;
          margin-right: auto;
          display: block;
        }
        
        .first-content img {
          margin-left: auto !important;
          margin-right: auto !important;
          display: block !important;
        }
        
        .first-content ul,
        .first-content ol {
          display: inline-block;
          text-align: left;
        }
        
        .last-content {
          text-align: left;
        }
        
        .last-content p,
        .last-content h1,
        .last-content h2,
        .last-content h3,
        .last-content h4,
        .last-content h5,
        .last-content h6,
        .last-content div,
        .last-content span,
        .last-content ul,
        .last-content ol {
          text-align: left;
        }
        
        .last-content img {
          margin-left: 0 !important;
          margin-right: 0 !important;
          display: block !important;
        }
        
        .store-content-wrapper p {
          margin-bottom: 0.75rem;
        }
        
        .store-content-wrapper h1,
        .store-content-wrapper h2,
        .store-content-wrapper h3,
        .store-content-wrapper h4,
        .store-content-wrapper h5,
        .store-content-wrapper h6 {
          margin-bottom: 0.75rem;
          margin-top: 0.75rem;
          font-weight: 600;
          color: #1f2937;
        }
        
        .store-content-wrapper h1:first-child,
        .store-content-wrapper h2:first-child,
        .store-content-wrapper h3:first-child,
        .store-content-wrapper h4:first-child,
        .store-content-wrapper p:first-child {
          margin-top: 0;
        }
        
        .store-content-wrapper ul,
        .store-content-wrapper ol {
          margin-bottom: 0.75rem;
          padding-left: 1.5rem;
        }
        
        .store-content-wrapper li {
          margin-bottom: 0.25rem;
        }
        
        .store-content-wrapper img {
          max-width: 100%;
          height: auto;
          border-radius: 0.5rem;
          margin: 0.75rem 0;
        }
        
        .store-content-wrapper a {
          color: #70B444;
          text-decoration: underline;
        }
      
        .store-content-wrapper a:hover {
          color: #538630;
        }
        
        .store-content-wrapper strong {
          font-weight: 700;
          color: #1f2937;
        }
        
        .store-content-wrapper em {
          font-style: italic;
        }
        
        .store-content-item {
          margin-bottom: 1rem;
        }
        
        .store-content-item:last-child {
          margin-bottom: 0;
        }
      `}</style>
    </div>
  );
}

export default StoreCardsDisplay;